import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useServices } from '@/shared/hooks/use-services';
import type { MerchantDetails } from '../types/get-merchant-details';

export type StoreStatus = {
  /** Loja está aberta agora (pedido imediato). */
  isOpen: boolean;
  /** Agendamento habilitado e com horários disponíveis. */
  isSchedulingAvailable: boolean;
  /** Loja fechada, porém aceitando pedidos agendados. */
  isClosedButSchedulable: boolean;
  /**
   * O usuário pode navegar/montar o pedido: verdadeiro quando a loja está
   * aberta OU quando fechada mas com agendamento disponível.
   */
  canBrowse: boolean;
  /** Consulta de agendamento em andamento. */
  isLoading: boolean;
};

/**
 * Deriva o "status efetivo" da loja combinando `is_open` do merchant com a
 * disponibilidade de agendamento. Uma loja fechada que aceita agendamento
 * deve permanecer navegável para que o usuário monte o pedido e agende.
 *
 * A consulta de agendamento só é disparada quando a loja está fechada — quando
 * aberta, o status já é conhecido sem custo de rede adicional.
 */
export function useStoreStatus(
  slug: string | undefined,
  store: MerchantDetails | undefined
): StoreStatus {
  const { getAvailableSchedule } = useServices();

  const isOpen = Boolean(store?.is_open ?? true);
  const shouldCheckSchedule = Boolean(slug) && Boolean(store) && !isOpen;

  const availableScheduleQuery = useQuery({
    // Mesma chave usada no checkout para reaproveitar o cache.
    queryKey: ['check-available-schedule', slug],
    queryFn: async () => await getAvailableSchedule.execute({ slug: slug! }),
    enabled: shouldCheckSchedule,
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  return useMemo<StoreStatus>(() => {
    const data = availableScheduleQuery.data;
    const isSchedulingAvailable = Boolean(
      data?.scheduling_enabled && data?.has_availability
    );
    const isClosedButSchedulable = !isOpen && isSchedulingAvailable;

    return {
      isOpen,
      isSchedulingAvailable,
      isClosedButSchedulable,
      canBrowse: isOpen || isSchedulingAvailable,
      isLoading: shouldCheckSchedule && availableScheduleQuery.isLoading,
    };
  }, [
    isOpen,
    shouldCheckSchedule,
    availableScheduleQuery.data,
    availableScheduleQuery.isLoading,
  ]);
}
